import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { sendInvitation, resetSendInvitation } from '../../../store/InvitationSlice';

const roles = ['admin', 'user'];

export default function InviteUserModal({ onClose }) {
  const dispatch = useDispatch();
  const tenantId = useSelector(
    (state) => state.buyerFormData.tenantId || state.auth.tenantId,
  );
  const { sendLoading, sendError, sendSuccess } = useSelector((state) => state.invitation);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Clear stale error/success state when modal opens
  useEffect(() => {
    dispatch(resetSendInvitation());
  }, [dispatch]);

  useEffect(() => {
    if (sendSuccess) {
      dispatch(resetSendInvitation());
      onClose();
    }
  }, [sendSuccess, dispatch, onClose]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProceed = () => {
    if (!email) return;
    dispatch(sendInvitation({ tenantId, email, role }));
  };

  return (
    <div
      className="overlay"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-[600px] mx-auto mt-[10vh] p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-center text-[#1D2739] mb-8">
          Invite user
        </h2>

        {/* Email */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[#1D2739] mb-2" style={{ textAlign: 'left' }}>
            Email
          </label>
          <input
            type="email"
            placeholder="johndoe@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-[#F5F5F5] text-sm text-[#1D2739] placeholder-[#ABABAB] outline-none border border-transparent focus:border-[#D0D0D0]"
            style={{ textAlign: 'left' }}
          />
        </div>

        {/* User role */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[#1D2739] mb-2" style={{ textAlign: 'left' }}>
            User role
          </label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-[#F5F5F5] text-sm text-[#1D2739] outline-none border border-transparent"
            >
              <span>{role}</span>
              <ChevronDown
                size={18}
                className="text-[#757575] transition-transform duration-200"
                style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-[#E5E5E5] rounded-lg shadow-md overflow-hidden">
                {roles.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => { setRole(r); setDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-[#F5F5F5] ${role === r ? 'text-[#1D2739] font-medium' : 'text-[#757575]'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Warning banner */}
        <div className="mb-8 px-4 py-4 rounded-lg bg-[#FDF6E3] border border-[#F0DFA0]">
          <p className="text-sm text-[#B45309] leading-snug" style={{ textAlign: 'left' }}>
            You've exhaused the required number of users on this plan. Upgrade to
            invite more users
          </p>
        </div>

        {/* API error */}
        {sendError && (
          <p className="text-sm text-red-500 mb-4" style={{ textAlign: 'left' }}>{sendError}</p>
        )}

        {/* Actions */}
        <button
          onClick={handleProceed}
          disabled={sendLoading || !email}
          className="w-full py-3 rounded-lg bg-[#F5E424] hover:bg-[#E0D020] text-base font-semibold text-[#1D2739] mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sendLoading ? 'Sending...' : 'Proceed'}
        </button>
        <button
          onClick={onClose}
          className="w-full py-2 text-sm text-[#1D2739] hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
